// src/scene/stages/ExperienceStage.tsx
/**
 * Experience Stage
 *
 * Work history displayed in horizontal timeline.
 * Shows career progression with consistent styling.
 */

import { Container, Text } from '@react-three/uikit'
import { useControls, folder } from 'leva'
import { StageShell } from '../StageShell'
import { useDesktopLayout } from '../useResponsiveLayout'
import {
  ui,
  Panel,
  VStack,
  Badge,
  Label,
  ListItem,
  PanelHeader,
  useHoverScale,
} from '../ui'

// ============================================================================
// EXPERIENCE DATA
// ============================================================================

const EXPERIENCE = [
  {
    title: 'Generative AI Engineer, Intern',
    company: 'AAA, ACE, ClubLabs, LLM Chatbot',
    period: 'Mar 2025 - Nov 2025',
    highlights: ['Migrated application to manual agent orchestration and deployment, responsible for and achieved multi-agent RAG and observability', 'Decreased backend observability dashboard load times by 84%'],
  },
  {
    title: 'Associate Software Engineer',
    company: 'AAA, ACE, ClubLabs, NextGen',
    period: 'Nov 2025 - Present',
    highlights: ['Leading production implementation of LLM-as-Judge evaluation platform from scratch', 'Reimplemtning business requirements using ReWOO orchestration to achieve targeted 25% reducation in LLM calls and async await'],
  },
]

// ============================================================================
// EXPERIENCE STAGE
// ============================================================================

// Stage 2 (Experience) UI defaults (mobile)
const STAGE2_UI_POSITION: [number, number, number] = [0, 0.22, 2]
const STAGE2_UI_ROTATION: [number, number, number] = [-0.15, 0, 0]

export function ExperienceStage() {
  const { isDesktopLayout } = useDesktopLayout()

  // Leva controls for Stage 2 UI position and rotation (mobile mode only)
  const {
    uiPosX, uiPosY, uiPosZ,
    uiRotX, uiRotY, uiRotZ,
  } = useControls('Stage 2 UI', {
    Position: folder({
      uiPosX: { value: STAGE2_UI_POSITION[0], min: -5, max: 5, step: 0.01 },
      uiPosY: { value: STAGE2_UI_POSITION[1], min: -5, max: 5, step: 0.01 },
      uiPosZ: { value: STAGE2_UI_POSITION[2], min: -5, max: 5, step: 0.01 },
    }),
    Rotation: folder({
      uiRotX: { value: STAGE2_UI_ROTATION[0], min: -Math.PI, max: Math.PI, step: 0.001 },
      uiRotY: { value: STAGE2_UI_ROTATION[1], min: -Math.PI, max: Math.PI, step: 0.001 },
      uiRotZ: { value: STAGE2_UI_ROTATION[2], min: -Math.PI, max: Math.PI, step: 0.001 },
    }),
  })

  // Desktop: wider horizontal layout, Mobile: vertical stack
  const panelWidth = isDesktopLayout ? 700 : 400
  const panelHeight = isDesktopLayout ? 320 : 585

  return (
    <StageShell
      stageId="experience"
      panelWidth={panelWidth}
      panelHeight={panelHeight}
      scale={0.7}
      panel={<ExperiencePanel isDesktop={isDesktopLayout} />}
      panelPosition={[uiPosX, uiPosY, uiPosZ]}
      panelRotation={[uiRotX, uiRotY, uiRotZ]}
    />
  )
}

// ============================================================================
// EXPERIENCE PANEL
// ============================================================================

interface ExperiencePanelProps {
  isDesktop: boolean
}

function ExperiencePanel({ isDesktop }: ExperiencePanelProps) {
  const panelWidth = isDesktop ? 700 : 400
  const panelHeight = isDesktop ? 320 : 585

  return (
    <Panel
      width={panelWidth}
      height={panelHeight}
      padding={ui.space.xl}
    >
      <PanelHeader
        title="Experience"
        badge={!isDesktop ? <Badge variant="accent">ACTIVE</Badge> : undefined}
      />

      {/* Desktop: horizontal row, Mobile: vertical stack */}
      <Container
        flexDirection={isDesktop ? 'row' : 'column'}
        gap={ui.space.lg}
        flexGrow={1}
      >
        {EXPERIENCE.map((exp, index) => (
          <ExperienceCard key={index} experience={exp} isDesktop={isDesktop} />
        ))}
      </Container>
    </Panel>
  )
}

// ============================================================================
// EXPERIENCE CARD
// ============================================================================

interface ExperienceCardProps {
  experience: typeof EXPERIENCE[0]
  isDesktop: boolean
}

function ExperienceCard({ experience, isDesktop }: ExperienceCardProps) {
  const { isActive, ...hoverScale } = useHoverScale()

  return (
    <Container
      flexDirection="column"
      flexBasis={isDesktop ? 0 : undefined}
      flexGrow={1}
      padding={ui.space.lg}
      gap={ui.space.lg}
      backgroundColor={isActive ? ui.colors.bg.card : ui.colors.bg.surface}
      borderRadius={ui.radius.md}
      borderTopWidth={1}
      borderBottomWidth={1}
      borderRightWidth={1}
      borderLeftWidth={3}
      borderColor={isActive ? ui.colors.primary : ui.colors.border.subtle}
      hover={{
        backgroundColor: ui.colors.bg.card,
        borderColor: ui.colors.primary,
      }}
      {...hoverScale}
    >
      {/* === HEADER === */}
      <Container flexDirection="column" gap={ui.space.sm}>
        {/* Period badge */}
        <Container
          backgroundColor={ui.colors.bg.card}
          paddingX={ui.space.sm}
          paddingY={ui.space.xs}
          borderRadius={ui.radius.sm}
          alignSelf="flex-start"
        >
          <Label>{experience.period}</Label>
        </Container>

        {/* Title */}
        <Text
          fontSize={ui.font.size.sm}
          fontWeight={ui.font.weight.bold}
          color={ui.colors.text.primary}
          lineHeight={ui.font.leading.tight}
        >
          {experience.title}
        </Text>

        {/* Company */}
        <Text
          fontSize={ui.font.size.xs}
          fontWeight={ui.font.weight.normal}
          color={ui.colors.text.accent}
        >
          {experience.company}
        </Text>
      </Container>

      {/* === BODY (flex to fill space) === */}
      <Container flexDirection="column" flexGrow={1}>
        <VStack gap={ui.space.sm}>
          {experience.highlights.map((highlight, idx) => (
            <ListItem key={idx}>{highlight}</ListItem>
          ))}
        </VStack>
      </Container>
    </Container>
  )
}

export default ExperienceStage
